import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, HelpCircle, X, Search, Filter } from 'lucide-react';

export default function FAQManagement() {
  const { user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [formData, setFormData] = useState({
    store_ids: [],
    question: '',
    answer: ''
  });

  useEffect(() => {
    Promise.all([fetchFaqs(), fetchStores()]).then(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [searchQuery, storeFilter]);

  const fetchFaqs = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (storeFilter) params.append('store_id', storeFilter);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/settings/faqs${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('S.S.S yüklenirken hata oluştu');
    }
  };

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Mağazalar yüklenirken hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.store_ids.length === 0) {
      toast.error('En az bir mağaza seçmelisiniz');
      return;
    }
    
    try {
      const url = editingFaq
        ? `${process.env.REACT_APP_BACKEND_URL}/api/settings/faqs/${editingFaq.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/settings/faqs`;
      
      const method = editingFaq ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingFaq ? 'S.S.S güncellendi' : 'S.S.S eklendi');
        setShowDialog(false);
        setEditingFaq(null);
        resetForm();
        fetchFaqs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      store_ids: faq.store_ids || [],
      question: faq.question,
      answer: faq.answer
    });
    setShowDialog(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Bu S.S.S\'yi silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/faqs/${faqId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('S.S.S silindi');
        fetchFaqs();
      } else {
        toast.error('Silme işlemi başarısız');
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const toggleStoreSelection = (storeId) => {
    setFormData(prev => {
      const newStoreIds = prev.store_ids.includes(storeId)
        ? prev.store_ids.filter(id => id !== storeId)
        : [...prev.store_ids, storeId];
      
      return { ...prev, store_ids: newStoreIds };
    });
  };

  const selectAllStores = () => {
    setFormData(prev => ({
      ...prev,
      store_ids: stores.map(s => s.id)
    }));
  };

  const deselectAllStores = () => {
    setFormData(prev => ({
      ...prev,
      store_ids: []
    }));
  };

  const resetForm = () => {
    setFormData({
      store_ids: [],
      question: '',
      answer: ''
    });
  };

  const getStoreNames = (storeIds) => {
    if (!storeIds || storeIds.length === 0) return 'Mağaza seçilmemiş';
    
    const names = storeIds
      .map(id => {
        const store = stores.find(s => s.id === id);
        return store ? store.store_name : null;
      })
      .filter(Boolean);
    
    return names.length > 0 ? names.join(', ') : 'Mağaza bulunamadı';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">S.S.S Yönetimi</h1>
              <p className="mt-1 text-sm text-gray-500">
                Mağazalarınız için sık sorulan soruları yönetin
              </p>
            </div>
            <button
              onClick={() => {
                setEditingFaq(null);
                resetForm();
                setShowDialog(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Yeni S.S.S
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="S.S.S içinde ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Store Filter */}
          <div className="sm:w-64">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none"
              >
                <option value="">Tüm Mağazalar</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.store_name} ({store.marketplace})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {faqs.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">S.S.S bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || storeFilter 
                ? 'Arama kriterlerinize uygun S.S.S bulunamadı. Filtreyi temizleyin veya yeni S.S.S ekleyin.'
                : 'İlk S.S.S\'nizi ekleyerek başlayın.'
              }
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setEditingFaq(null);
                  resetForm();
                  setShowDialog(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-5 h-5 mr-2" />
                S.S.S Ekle
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start">
                      <HelpCircle className="w-5 h-5 text-orange-500 mt-1 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {faq.question}
                        </h3>
                        <p className="text-gray-600 mb-3">{faq.answer}</p>
                        <div className="flex items-center text-sm text-gray-500">
                          <span className="font-medium mr-2">Mağazalar:</span>
                          <span>{getStoreNames(faq.store_ids)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-md"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingFaq ? 'S.S.S\'yi Düzenle' : 'Yeni S.S.S Ekle'}
                </h2>
                <button
                  onClick={() => {
                    setShowDialog(false);
                    setEditingFaq(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Bu S.S.S hangi mağazalara ait? *
                    </label>
                    <div className="space-x-2">
                      <button
                        type="button"
                        onClick={selectAllStores}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Tümünü Seç
                      </button>
                      <button
                        type="button"
                        onClick={deselectAllStores}
                        className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Temizle
                      </button>
                    </div>
                  </div>
                  
                  {stores.length === 0 ? (
                    <p className="text-sm text-red-600">
                      Önce mağaza eklemelisiniz!
                    </p>
                  ) : (
                    <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                      <div className="space-y-2">
                        {stores.map((store) => (
                          <label key={store.id} className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.store_ids.includes(store.id)}
                              onChange={() => toggleStoreSelection(store.id)}
                              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">
                              {store.store_name} ({store.marketplace})
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.store_ids.length} mağaza seçildi
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Soru *
                  </label>
                  <input
                    type="text"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Örn: Kargo ücreti ne kadar?"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cevap *
                  </label>
                  <textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows="4"
                    placeholder="Örn: 150 TL üzeri alışverişlerde kargo ücretsizdir."
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDialog(false);
                      setEditingFaq(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                    disabled={stores.length === 0}
                  >
                    {editingFaq ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
